import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('cbxtv_token'));
  const [loading, setLoading] = useState(true);

  // Load user from token on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await authService.getProfile(token);
          setUser(response.data);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('cbxtv_token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    loadUser();
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await authService.login({ email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('cbxtv_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const response = await authService.register({ email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('cbxtv_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const googleLogin = useCallback(async (googleToken, googleId, email, name, avatar) => {
    const response = await authService.googleAuth({ 
      google_token: googleToken, 
      google_id: googleId, 
      email, 
      name, 
      avatar 
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('cbxtv_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('cbxtv_token');
    setToken(null);
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (data) => {
    if (!token) throw new Error('Not authenticated');
    const response = await authService.updateProfile(data, token);
    setUser(response.data);
    return response.data;
  }, [token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    googleLogin,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
